/**
 * The people Compass has learned, on the My People screen.
 *
 * They were invisible until now, and not by accident: /life-graph reads the
 * graph's `life_nodes`, which a lifecoach-routed user never writes to — their
 * turns are handled by the sidecar, so everyone they mention lands in ITS
 * entity axis instead. Measured on a live account 2026-08-20: twelve people
 * engine-side, one seeded `identity` in the graph, and a screen that said
 * "No one here yet".
 *
 * Two things this section does that a plain list would not:
 *
 *   - it is SEARCHABLE by name, relationship and tag, which is the whole
 *     reason tags exist here;
 *   - it separates people who were NAMED from people who were only ever
 *     referred to by their relationship ("my daughter", "spouse", "kids").
 *     Those are placeholders the axis produces by design, and showing them
 *     mixed in reads as four people when it is at most two.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight, Combine, Loader2, Search, Sparkles, UserRound, X } from 'lucide-react'
import { Card, CardContent } from '../ui/Card'
import { TagChips } from './TagEditor'
import { useTheme } from '../providers/ThemeProvider'
import { PersonDossier } from '../diary/PersonDossier'
import { hortonV3Service, type CapturedPerson } from '../../services/hortonV3Service'
import { BRAND } from '../../config/brand'

function PersonRow({ person, onMerge }: {
  person: CapturedPerson
  /** Start a merge with this person as the one being folded IN. Offered on
   *  the list because that is where a duplicate is visible — you see the two
   *  of them side by side, which is the moment you know. */
  onMerge?: () => void
}) {
  const recent = (person.notes ?? []).slice(0, 2)
  const { isDiary } = useTheme()

  // Diary theme: the engine-captured person renders as a dossier folder.
  // Same link, same merge affordance; the withheld count stays a quiet
  // number and never becomes content.
  if (isDiary) {
    return (
      <div className="relative group/row">
        {onMerge && (
          <button
            onClick={(e) => { e.preventDefault(); onMerge() }}
            title={`${person.name} is the same person as someone else here`}
            aria-label={`Merge ${person.name} into someone else`}
            className="absolute right-9 top-6 z-10 p-1.5 rounded
                       opacity-0 group-hover/row:opacity-100 focus:opacity-100
                       hover:bg-black/5 transition-opacity"
            style={{ color: 'var(--ink-faded)' }}
          >
            <Combine className="w-4 h-4" />
          </button>
        )}
        <Link to={`/my-people/person/${person.id}`} className="block group">
          <PersonDossier
            id={person.id}
            name={person.name}
            relation={person.relation_to_user}
            aliases={person.aliases ?? []}
            summary={person.summary}
            notes={person.summary ? [] : recent}
            tags={person.tags ?? []}
            itemCount={(person.items ?? []).length}
            corner={<ChevronRight className="w-4 h-4 shrink-0 mt-1" style={{ color: 'var(--ink-faded)' }} />}
          />
        </Link>
      </div>
    )
  }

  return (
    <div className="relative group/row">
      {onMerge && (
        <button
          onClick={(e) => { e.preventDefault(); onMerge() }}
          title={`${person.name} is the same person as someone else here`}
          aria-label={`Merge ${person.name} into someone else`}
          className="absolute right-9 top-4 z-10 p-1.5 rounded text-muted
                     opacity-0 group-hover/row:opacity-100 focus:opacity-100
                     hover:bg-surface-alt hover:text-brand transition-opacity"
        >
          <Combine className="w-4 h-4" />
        </button>
      )}
    <Link to={`/my-people/person/${person.id}`} className="block group">
      <Card className="overflow-hidden transition-colors group-hover:border-brand/40">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-xl bg-info/10 text-brand flex-shrink-0">
              <UserRound className="w-4.5 h-4.5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-medium text-primary truncate">{person.name}</h3>
                {(person.items ?? []).length > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs text-muted">
                    <Sparkles className="w-3 h-3" /> {(person.items ?? []).length}
                  </span>
                )}
              </div>
              {(person.relation_to_user || (person.aliases ?? []).length > 0) && (
                <p className="text-sm text-muted truncate">
                  {person.relation_to_user}
                  {person.relation_to_user && (person.aliases ?? []).length > 0 ? ' · ' : ''}
                  {(person.aliases ?? []).length > 0 ? `also “${(person.aliases ?? []).join('”, “')}”` : ''}
                </p>
              )}
              {/* The summary leads when there is one: a card should read as
                  a person, not as a list of fragments. The lines underneath
                  are still the evidence, and the page shows all of them. */}
              {person.summary ? (
                <p className="mt-1.5 text-sm text-secondary line-clamp-3">{person.summary}</p>
              ) : recent.length > 0 && (
                <ul className="mt-2 space-y-0.5">
                  {recent.map((n, i) => (
                    <li key={i} className="text-sm text-secondary truncate">· {n}</li>
                  ))}
                </ul>
              )}
              {(person.tags ?? []).length > 0 && (
                <div className="mt-2"><TagChips tags={person.tags ?? []} /></div>
              )}
            </div>
            <ChevronRight className="w-4 h-4 text-muted flex-shrink-0 mt-1" />
          </div>
        </CardContent>
      </Card>
    </Link>
    </div>
  )
}

/**
 * "Who is this really?" — the second half of a merge, and the half that has to
 * be unmistakable, because a merge cannot be undone. The person being folded
 * IN is named in the question; the one they become is chosen from a list they
 * can search.
 */
function MergePicker({ subject, candidates, onCancel, onDone }: {
  subject: CapturedPerson
  candidates: CapturedPerson[]
  onCancel: () => void
  onDone: () => void
}) {
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return candidates.filter((p) => !needle ||
      [p.name, p.relation_to_user ?? '', ...(p.aliases ?? [])]
        .join(' ').toLowerCase().includes(needle))
  }, [candidates, q])

  const merge = async (into: CapturedPerson) => {
    setBusy(into.id)
    setError(null)
    // keep = the one they stay as, drop = the duplicate being folded in
    const r = await hortonV3Service.mergePeople(into.id, subject.id)
    setBusy(null)
    if (!r.ok) { setError(r.error ?? 'that did not merge'); return }
    onDone()
  }

  return (
    <Card className="border-brand/40">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <p className="text-sm text-primary font-medium">
              Who is “{subject.name}” really?
            </p>
            <p className="text-xs text-muted mt-0.5">
              Everything recorded under “{subject.name}” moves onto the person you
              pick, and “{subject.name}” becomes another name for them. {BRAND.assistant}
              remembers this, so it won’t split them again. It can’t be undone.
            </p>
          </div>
          <button onClick={onCancel} aria-label="Cancel merge"
                  className="p-1 rounded hover:bg-surface-alt text-muted">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="relative">
          <Search className="w-3.5 h-3.5 text-muted absolute left-2 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus
                 placeholder="Search people"
                 className="w-full pl-7 pr-2 py-1.5 rounded border border-soft bg-surface
                            text-sm text-primary placeholder:text-muted
                            focus:outline-none focus:ring-1 focus:ring-brand" />
        </div>

        <div className="max-h-64 overflow-y-auto space-y-1">
          {shown.map((p) => (
            <button key={p.id} onClick={() => void merge(p)} disabled={busy !== null}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded
                               text-left text-sm hover:bg-surface-alt disabled:opacity-50">
              <UserRound className="w-4 h-4 text-brand flex-shrink-0" />
              <span className="text-primary">{p.name}</span>
              {p.relation_to_user && <span className="text-xs text-muted">· {p.relation_to_user}</span>}
              {busy === p.id && <Loader2 className="w-3.5 h-3.5 animate-spin ml-auto" />}
            </button>
          ))}
          {shown.length === 0 && <p className="text-xs text-muted px-2">Nobody matches that.</p>}
        </div>
        {error && <p className="text-sm text-bad">{error}</p>}
      </CardContent>
    </Card>
  )
}

export function CapturedPeople({ people, loading, onChanged }: {
  people: CapturedPerson[]
  loading: boolean
  /** A merge changes the list; the page owns the data, so it refetches. */
  onChanged?: () => void
}) {
  const [q, setQ] = useState('')
  const [tag, setTag] = useState<string | null>(null)
  const [merging, setMerging] = useState<CapturedPerson | null>(null)

  const allTags = useMemo(() => {
    const seen = new Map<string, number>()
    for (const p of people) for (const t of p.tags ?? []) seen.set(t, (seen.get(t) ?? 0) + 1)
    return [...seen.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([t]) => t)
  }, [people])

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return people.filter((p) => {
      if (tag && !(p.tags ?? []).some((t) => t === tag)) return false
      if (!needle) return true
      // Name, the words they used for the relationship, every alias, and the
      // tags — the four things someone would actually type to find a person.
      return [p.name, p.relation_to_user ?? '', ...(p.aliases ?? []), ...(p.tags ?? [])]
        .join(' ').toLowerCase().includes(needle)
    })
  }, [people, q, tag])

  const named = shown.filter((p) => p.named)
  const unnamed = shown.filter((p) => !p.named)

  if (loading || people.length === 0) return null

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="text-sm font-medium text-muted uppercase tracking-wide flex items-center gap-2">
          <UserRound className="w-4 h-4" /> From your conversations
        </h2>
        <span className="text-xs text-muted">{people.length}</span>
        <div className="ml-auto relative">
          <Search className="w-3.5 h-3.5 text-muted absolute left-2 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search people"
                 className="pl-7 pr-2 py-1 rounded border border-soft bg-surface text-xs
                            text-primary placeholder:text-muted focus:outline-none
                            focus:ring-1 focus:ring-brand w-40" />
        </div>
      </div>

      {allTags.length > 0 && (
        <TagChips tags={allTags} active={(t) => t === tag}
                  onClick={(t) => setTag(tag === t ? null : t)} />
      )}

      {merging && (
        <MergePicker subject={merging}
                     candidates={people.filter((p) => p.id !== merging.id)}
                     onCancel={() => setMerging(null)}
                     onDone={() => { setMerging(null); onChanged?.() }} />
      )}

      {shown.length === 0 ? (
        <p className="text-sm text-muted">Nobody here matches that.</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {named.map((p) => (
            <PersonRow key={p.id} person={p} onMerge={() => setMerging(p)} />
          ))}
        </div>
      )}

      {unnamed.length > 0 && (
        <div className="pt-2">
          <p className="text-xs text-muted mb-2">
            Mentioned only by relationship — you haven’t named them yet. Open one to
            give them a name, or merge it into the person it belongs to.
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            {unnamed.map((p) => (
              <PersonRow key={p.id} person={p} onMerge={() => setMerging(p)} />
            ))}
          </div>
        </div>
      )}
    </section>
  )
}
